// Mirror of backend/app/services/entitlements.py. The BACKEND is the true
// enforcer (returns 403); this module is UX-only. Keep in lockstep.

/** プランの包含順。上位ほど多くの機能を含む。未知/未購読は free（0）扱い。 */
export const PLAN_ORDER = {
  free: 0,
  starter: 1,
  pro: 2,
  business: 3,
} as const;

export type PlanCode = keyof typeof PLAN_ORDER;

/** ゲート対象の機能キー（backend FEATURE_MIN_TIER と一致させる）。 */
export type Feature =
  | "slack"
  | "line"
  | "stocky_migration"
  | "reorder"
  | "auto_draft"
  | "history_30"
  | "inventory_analytics"
  | "inventory_adjustment"
  | "stocktake"
  | "inventory_transfer"
  | "multi_supplier"
  | "landed_cost"
  | "reports"
  | "support_call";

/**
 * 機能 → それを解禁する最小プラン。
 * backend `entitlements.py` の FEATURE_MIN_TIER と1:1で一致させる（2026-07-12
 * 再バランス反映）: `stocky_migration` は Free ウェッジ（無料開放）、`reorder`
 * と新設 `auto_draft` は Starter+。2026-07-13 通知階層見直しで `slack` は
 * Starter→Pro、新設 `line` も Pro（メールは非ゲート＝Starter ベースラインなので
 * キーを持たない）。ここが backend とズレるとゲートUIの必要プラン表示が実課金
 * 判定（403）と食い違う。
 */
export const FEATURE_MIN_TIER: Record<Feature, PlanCode> = {
  slack: "pro",
  line: "pro",
  stocky_migration: "free",
  reorder: "starter",
  auto_draft: "starter",
  history_30: "pro",
  inventory_analytics: "pro",
  inventory_adjustment: "free",
  stocktake: "starter",
  inventory_transfer: "business",
  multi_supplier: "pro",
  landed_cost: "pro",
  reports: "pro",
  // Business 電話サポート。旧 backend の予約 URL 応答にも同じゲートが残るが、
  // 現行 SPA は予約 UI を表示しない。
  support_call: "business",
};

/** プランの序列。null・未知コードは free（0）に落とす（backend `_rank` と同じ）。 */
function rank(planCode: string | null): number {
  if (planCode !== null && planCode in PLAN_ORDER) {
    return PLAN_ORDER[planCode as PlanCode];
  }
  return 0;
}

/** `planCode` が `feature` を使えるか（rank が最小ティア以上か）。 */
export function planAllows(planCode: string | null, feature: Feature): boolean {
  return rank(planCode) >= rank(FEATURE_MIN_TIER[feature]);
}

/** 機能の最小ティアの表示ラベル（"Starter"/"Pro"/"Business"）。 */
export function requiredPlanLabel(feature: Feature): string {
  const tier = FEATURE_MIN_TIER[feature];
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}
